import { useRef, useState, type ReactNode } from 'react'
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View
} from 'react-native'
import * as Clipboard from 'expo-clipboard'
import Constants from 'expo-constants'
import { useTranslation } from 'react-i18next'
import { SafeAreaView } from 'react-native-safe-area-context'

import { RUNTIME_ENVIRONMENTS, type RuntimeEnvironment } from '@/constants/config'
import { marketPresentation } from '@/config/marketPresentation'
import {
  exportNativeDiagnostics,
  getNativeDiagnosticsForClipboard,
  isNativeDiagnosticsAvailable
} from '@/utils/nativeDiagnostics'
import { useRuntimeEnvironment } from '@/components/providers/RuntimeEnvironment'

const environmentOrder: RuntimeEnvironment[] = ['production', 'staging', 'localhost']
const UNLOCK_TAPS = 10
const TAP_WINDOW_MS = 5_000
type CommitCopyStatus = 'idle' | 'copying' | 'copied' | 'failed'
type DiagnosticsExportStatus = 'cancelled' | 'exporting' | 'failed' | 'idle' | 'saved' | 'shared'
export default function EnvironmentSwitcher({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const { environment, selectEnvironment } = useRuntimeEnvironment()
  const buildCommit = Constants.expoConfig?.extra?.gitInfo?.commitHash ?? 'unknown'
  const [visible, setVisible] = useState(false)
  const [saving, setSaving] = useState<RuntimeEnvironment | null>(null)
  const [commitCopyStatus, setCommitCopyStatus] = useState<CommitCopyStatus>('idle')
  const [diagnosticsCopyStatus, setDiagnosticsCopyStatus] = useState<CommitCopyStatus>('idle')
  const [diagnosticsExportStatus, setDiagnosticsExportStatus] = useState<DiagnosticsExportStatus>('idle')
  const taps = useRef({ count: 0, lastTap: 0 })
  const copyAttempt = useRef(0)
  const commitCopyLabel =
    commitCopyStatus === 'copying'
      ? t('ui:copying')
      : commitCopyStatus === 'copied'
        ? t('ui:copySuccess')
        : commitCopyStatus === 'failed'
          ? t('ui:copyFailed')
          : t('ui:copy')
  const diagnosticsCopyLabel =
    diagnosticsCopyStatus === 'copying'
      ? t('ui:copying')
      : diagnosticsCopyStatus === 'copied'
        ? t('ui:copySuccess')
        : diagnosticsCopyStatus === 'failed'
          ? t('ui:copyFailed')
          : t('ui:copy')
  const diagnosticsLabels: Record<DiagnosticsExportStatus, string> = {
    cancelled: t('ui:diagnostics.cancelled'),
    exporting: t('ui:diagnostics.preparing'),
    failed: t('ui:diagnostics.exportFailed'),
    idle: t(Platform.OS === 'android' ? 'ui:diagnostics.save' : 'ui:diagnostics.share'),
    saved: t('ui:diagnostics.saved'),
    shared: t('ui:diagnostics.shared')
  }
  const diagnosticsLabel = diagnosticsLabels[diagnosticsExportStatus]

  const handleSecretTap = () => {
    const now = Date.now()
    const count = now - taps.current.lastTap > TAP_WINDOW_MS ? 1 : taps.current.count + 1
    taps.current = { count, lastTap: now }

    if (count >= UNLOCK_TAPS) {
      taps.current = { count: 0, lastTap: 0 }
      copyAttempt.current += 1
      setCommitCopyStatus('idle')
      setDiagnosticsCopyStatus('idle')
      setDiagnosticsExportStatus('idle')
      setVisible(true)
    }
  }

  const handleCopyCommit = async () => {
    const attempt = copyAttempt.current + 1
    copyAttempt.current = attempt
    setCommitCopyStatus('copying')
    try {
      await Clipboard.setStringAsync(buildCommit)
      if (copyAttempt.current === attempt) setCommitCopyStatus('copied')
    } catch {
      if (copyAttempt.current === attempt) setCommitCopyStatus('failed')
    }
  }

  const handleExportDiagnostics = async () => {
    setDiagnosticsExportStatus('exporting')
    try {
      const result = await exportNativeDiagnostics()
      setDiagnosticsExportStatus(result)
    } catch (error) {
      console.error('Unable to export native diagnostics', error)
      setDiagnosticsExportStatus('failed')
    }
  }

  const handleCopyDiagnostics = async () => {
    setDiagnosticsCopyStatus('copying')
    try {
      const content = await getNativeDiagnosticsForClipboard()
      await Clipboard.setStringAsync(content)
      setDiagnosticsCopyStatus('copied')
    } catch (error) {
      console.error('Unable to copy native diagnostics', error)
      setDiagnosticsCopyStatus('failed')
    }
  }

  const handleSelect = async (selectedEnvironment: RuntimeEnvironment) => {
    if (selectedEnvironment === environment) {
      setVisible(false)
      return
    }

    setSaving(selectedEnvironment)
    try {
      await selectEnvironment(selectedEnvironment)
      setVisible(false)
    } finally {
      setSaving(null)
    }
  }

  return (
    <>
      <Pressable accessibilityLabel={marketPresentation.branding.displayName} onPress={handleSecretTap}>
        {children}
      </Pressable>
      <Modal animationType={'fade'} onRequestClose={() => setVisible(false)} transparent visible={visible}>
        <SafeAreaView style={styles.backdrop}>
          <ScrollView
            contentContainerStyle={styles.backdropContent}
            keyboardShouldPersistTaps={'handled'}
            style={styles.backdropScroll}
          >
            <View style={styles.dialog}>
              <Text style={styles.title}>{t('ui:backendEnvironment')}</Text>
              <Text style={styles.description}>{t('ui:environmentSwitchDescription')}</Text>

              <View style={styles.options}>
                {environmentOrder.map((item) => {
                  const config = RUNTIME_ENVIRONMENTS[item]
                  const selected = item === environment
                  const isSaving = item === saving

                  return (
                    <TouchableOpacity
                      accessibilityRole={'radio'}
                      accessibilityState={{ checked: selected, disabled: saving !== null }}
                      activeOpacity={0.7}
                      disabled={saving !== null}
                      key={item}
                      onPress={() => handleSelect(item)}
                      style={[
                        styles.option,
                        item !== 'localhost' && styles.optionSpacing,
                        selected && styles.selectedOption
                      ]}
                    >
                      <View style={styles.optionText}>
                        <Text style={[styles.optionLabel, selected && styles.selectedLabel]}>
                          {t(`ui:runtimeEnvironment.${item}`)}
                        </Text>
                        <Text numberOfLines={1} style={styles.optionUrl}>
                          {config.apiUrl}
                        </Text>
                      </View>
                      <View style={[styles.radio, selected && styles.selectedRadio]}>
                        {isSaving ? (
                          <ActivityIndicator color={selected ? '#FFFFFF' : '#3477DC'} size={'small'} />
                        ) : selected ? (
                          <Text style={styles.radioCheck}>{'✓'}</Text>
                        ) : null}
                      </View>
                    </TouchableOpacity>
                  )
                })}
              </View>

              <Pressable
                accessibilityRole={'button'}
                disabled={saving !== null}
                onPress={() => setVisible(false)}
                style={styles.cancel}
              >
                {({ pressed }) => <Text style={[styles.cancelText, pressed && styles.pressed]}>{t('ui:cancel')}</Text>}
              </Pressable>
              <TouchableOpacity
                accessibilityLabel={t('ui:buildCommit')}
                accessibilityRole={'button'}
                accessibilityState={{ disabled: commitCopyStatus === 'copying' }}
                accessibilityValue={{ text: commitCopyLabel }}
                activeOpacity={0.7}
                disabled={commitCopyStatus === 'copying'}
                onPress={handleCopyCommit}
                style={styles.commitButton}
              >
                <View style={styles.commitText}>
                  <Text style={styles.commitLabel}>{t('ui:buildCommit')}</Text>
                  <Text selectable style={styles.buildCommit}>
                    {buildCommit}
                  </Text>
                </View>
                <Text accessibilityLiveRegion={'polite'} style={styles.copyCommit}>
                  {commitCopyLabel}
                </Text>
              </TouchableOpacity>
              {isNativeDiagnosticsAvailable && (
                <View style={styles.diagnosticsButton}>
                  <View style={styles.commitText}>
                    <Text style={styles.commitLabel}>{t('ui:diagnostics.title')}</Text>
                    <Text style={styles.diagnosticsDescription}>
                      {t(Platform.OS === 'android' ? 'ui:diagnostics.androidDescription' : 'ui:diagnostics.iosDescription', {
                        brand: marketPresentation.branding.displayName
                      })}
                    </Text>
                  </View>
                  <View style={styles.diagnosticsActions}>
                    <TouchableOpacity
                      accessibilityLabel={t('ui:diagnostics.exportLog')}
                      accessibilityRole={'button'}
                      accessibilityState={{ disabled: diagnosticsExportStatus === 'exporting' }}
                      activeOpacity={0.7}
                      disabled={diagnosticsExportStatus === 'exporting'}
                      onPress={handleExportDiagnostics}
                      style={styles.diagnosticsAction}
                    >
                      <Text accessibilityLiveRegion={'polite'} style={styles.diagnosticsActionText}>
                        {diagnosticsLabel}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      accessibilityLabel={t('ui:diagnostics.copyLog')}
                      accessibilityRole={'button'}
                      accessibilityState={{ disabled: diagnosticsCopyStatus === 'copying' }}
                      activeOpacity={0.7}
                      disabled={diagnosticsCopyStatus === 'copying'}
                      onPress={handleCopyDiagnostics}
                      style={styles.diagnosticsAction}
                    >
                      <Text accessibilityLiveRegion={'polite'} style={styles.diagnosticsActionText}>
                        {diagnosticsCopyLabel}
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              )}
            </View>
          </ScrollView>
        </SafeAreaView>
      </Modal>
    </>
  )
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.45)'
  },
  backdropScroll: {
    width: '100%'
  },
  backdropContent: {
    minHeight: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24
  },
  dialog: {
    width: '100%',
    maxWidth: 420,
    alignItems: 'stretch',
    borderRadius: 18,
    backgroundColor: '#FFFFFF',
    padding: 20
  },
  title: {
    color: '#171717',
    fontSize: 20,
    fontWeight: '700'
  },
  description: {
    marginTop: 6,
    color: '#6B7280',
    fontSize: 14
  },
  options: {
    width: '100%',
    alignSelf: 'stretch',
    marginTop: 18
  },
  option: {
    width: '100%',
    alignSelf: 'stretch',
    minHeight: 78,
    flexDirection: 'row',
    alignItems: 'center',
    borderColor: '#D1D5DB',
    borderRadius: 14,
    borderWidth: 1.5,
    backgroundColor: '#F9FAFB',
    paddingHorizontal: 14,
    paddingVertical: 12
  },
  optionSpacing: {
    marginBottom: 10
  },
  selectedOption: {
    borderColor: '#3477DC',
    borderWidth: 2,
    backgroundColor: '#EAF2FF'
  },
  optionText: {
    minWidth: 0,
    flexGrow: 1,
    flexShrink: 1
  },
  optionLabel: {
    marginBottom: 4,
    color: '#171717',
    fontSize: 16,
    fontWeight: '600'
  },
  selectedLabel: {
    color: '#2868C7'
  },
  optionUrl: {
    color: '#6B7280',
    fontSize: 12
  },
  radio: {
    width: 28,
    height: 28,
    marginLeft: 12,
    alignItems: 'center',
    justifyContent: 'center',
    borderColor: '#9CA3AF',
    borderRadius: 14,
    borderWidth: 2,
    backgroundColor: '#FFFFFF'
  },
  selectedRadio: {
    borderColor: '#3477DC',
    backgroundColor: '#3477DC'
  },
  radioCheck: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '700',
    lineHeight: 20
  },
  cancel: {
    alignItems: 'center',
    marginTop: 16,
    paddingVertical: 8
  },
  cancelText: {
    color: '#3477DC',
    fontSize: 16,
    fontWeight: '600'
  },
  commitButton: {
    minHeight: 58,
    marginTop: 12,
    flexDirection: 'row',
    alignItems: 'center',
    borderColor: '#D1D5DB',
    borderRadius: 12,
    borderWidth: 1,
    backgroundColor: '#F9FAFB',
    paddingHorizontal: 12,
    paddingVertical: 10
  },
  commitText: {
    minWidth: 0,
    flexGrow: 1,
    flexShrink: 1
  },
  commitLabel: {
    marginBottom: 4,
    color: '#6B7280',
    fontSize: 12,
    fontWeight: '600'
  },
  buildCommit: {
    color: '#9CA3AF',
    fontFamily: 'monospace',
    fontSize: 10
  },
  copyCommit: {
    marginLeft: 12,
    color: '#3477DC',
    fontSize: 14,
    fontWeight: '700'
  },
  diagnosticsButton: {
    minHeight: 58,
    marginTop: 10,
    borderColor: '#D1D5DB',
    borderRadius: 12,
    borderWidth: 1,
    backgroundColor: '#F9FAFB',
    paddingHorizontal: 12,
    paddingVertical: 10
  },
  diagnosticsDescription: {
    color: '#6B7280',
    fontSize: 12
  },
  diagnosticsActions: {
    flexDirection: 'row',
    gap: 6,
    marginTop: 8
  },
  diagnosticsAction: {
    flex: 1,
    minHeight: 40,
    minWidth: 48,
    alignItems: 'center',
    justifyContent: 'center',
    borderColor: '#BFDBFE',
    borderRadius: 8,
    borderWidth: 1,
    backgroundColor: '#EFF6FF',
    paddingHorizontal: 8
  },
  diagnosticsActionText: {
    color: '#3477DC',
    fontSize: 13,
    fontWeight: '700'
  },
  pressed: {
    opacity: 0.55
  }
})
